import { useEffect, useState } from "react";

import MobileFrame from "../../../components/MobileFrame";

import {
    Loader2,
    AlertCircle,
} from "lucide-react";

import {
    getDownPaymentSession,
    initiatePublicDownPayment,
} from "../../../api/downpayment.api";

import { ApiError } from "../../../api/ApiError";

interface DownPaymentJourney {
    journeyId: string;
    orderId: string;
    customerName: string;
    customerEmail: string;
    dpAmount: number;
    steps?: {
        DOWNPAYMENT?: {
            status?: string;
        };
    };
}

const PoweredByFooter = ({
    className = "",
}: {
    className?: string;
}) => (
    <p
        className={`text-center text-[11px] font-semibold tracking-wide text-gray-400 ${className}`}
    >
        CleverPe
    </p>
);

const DownPayment = () => {

    const [journey, setJourney] =
        useState<DownPaymentJourney | null>(null);

    const [isLoading, setIsLoading] =
        useState(true);

    const [isPaying, setIsPaying] =
        useState(false);

    const [error, setError] =
        useState<string | null>(null);


    const [paymentError, setPaymentError] =
        useState<string | null>(null);

    /*
     * =========================================================
     * GET ORDER ID + TOKEN FROM URL
     * =========================================================
     *
     * Example:
     *
     * /downPayment?orderId=CP7143814669&token=xxxxx
     */

    const params = new URLSearchParams(
        window.location.search
    );

    const orderId =
        params.get("orderId");

    const token =
        params.get("token");

    /*
     * =========================================================
     * LOAD PUBLIC DOWN PAYMENT SESSION
     * =========================================================
     */

    useEffect(() => {

        const loadSession = async () => {

            if (!orderId || !token) {

                setError(
                    "Invalid or expired payment link."
                );

                setIsLoading(false);

                return;
            }

            try {

                setIsLoading(true);

                setError(null);

                const response =
                    await getDownPaymentSession(
                        orderId,
                        token
                    );

                if (
                    !response?.success ||
                    !response?.journey
                ) {
                    throw new Error(
                        "Unable to load payment details."
                    );
                }

                const latestJourney =
                    response.journey;

                const downPaymentStatus =
                    latestJourney
                        ?.steps
                        ?.DOWNPAYMENT
                        ?.status;

                if (
                    downPaymentStatus ===
                    "COMPLETED"
                ) {

                    setError(
                        "Down payment has already been completed."
                    );

                    return;
                }

                setJourney(
                    latestJourney
                );

            } catch (error) {

                let message =
                    "Unable to load your payment details.";

                if (error instanceof ApiError) {

                    if (
                        error.status >= 500 &&
                        error.status < 600
                    ) {
                        message =
                            "Something went wrong. Please try again later.";
                    } else {
                        message =
                            error.message?.trim() ||
                            message;
                    }
                }

                setError(message);

            } finally {

                setIsLoading(false);

            }
        };

        loadSession();

    }, [orderId, token]);

    /*
     * =========================================================
     * PAY NOW
     * =========================================================
     *
     * Only orderId + short-lived token are sent.
     *
     * Backend gets:
     * - customer name
     * - customer email
     * - mobile
     * - DP amount
     *
     * directly from DB.
     */

    const handlePayNow = async () => {

        if (
            isPaying ||
            !journey ||
            !orderId ||
            !token
        ) {
            return;
        }

        try {

            setIsPaying(true);
            setPaymentError(null);

            const response =
                await initiatePublicDownPayment(
                    {
                        orderId: journey.orderId,
                        amount: journey.dpAmount,
                        firstname: journey.customerName,
                        email: journey.customerEmail,
                    },
                    token
                );

            if (
                !response?.success ||
                !response?.paymentLink
            ) {
                throw new Error(
                    "Payment link was not generated."
                );
            }

            window.location.href =
                response.paymentLink;

        } catch (error) {

            let message =
                "Unable to initiate payment.";

            if (error instanceof ApiError) {

                if (
                    error.status >= 500 &&
                    error.status < 600
                ) {
                    message =
                        "Something went wrong. Please try again later.";
                } else {
                    message =
                        error.message?.trim() ||
                        message;
                }
            }

            setPaymentError(message);

            setIsPaying(false);
        }
    };

    /*
     * =========================================================
     * FORMAT AMOUNT
     * =========================================================
     */

    const formatAmount = (
        amount: number
    ) =>
        `₹${amount.toLocaleString("en-IN")}`;

    /*
     * =========================================================
     * LOADING STATE
     * =========================================================
     */

    if (isLoading) {

        return (
            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >
                <div className="flex h-full items-center justify-center bg-white px-6">

                    <div className="flex flex-col items-center">

                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50">
                            <Loader2 className="h-7 w-7 animate-spin text-indigo-500" />
                        </div>

                        <p className="mt-4 text-sm font-semibold text-gray-800">
                            Loading payment details
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                            Please wait a moment...
                        </p>

                    </div>

                </div>
            </MobileFrame>
        );
    }

    /*
     * =========================================================
     * ERROR STATE
     * =========================================================
     */

    if (error || !journey) {

        return (
            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >
                <div className="relative flex h-full flex-col items-center justify-center bg-white px-8 text-center">

                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
                        <AlertCircle className="h-8 w-8 text-red-500" />
                    </div>

                    <p className="mt-5 text-[14px] font-semibold text-gray-900">
                        Unable to load payment
                    </p>

                    <p className="mt-2 max-w-[280px] text-[12px] leading-snug text-gray-500">
                        {error ||
                            "Unable to load your payment details. Please use the latest payment link."}
                    </p>

                    <PoweredByFooter className="absolute bottom-6" />

                </div>
            </MobileFrame>
        );
    }

    /*
     * =========================================================
     * MAIN DOWN PAYMENT SCREEN
     * =========================================================
     */

    return (
        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >
            <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                {/* Background decoration */}

                <div className="absolute -left-20 -top-24 h-64 w-64 rounded-full bg-purple-200 opacity-20 blur-3xl" />

                <div className="absolute -bottom-24 -right-20 h-64 w-64 rounded-full bg-blue-200 opacity-20 blur-3xl" />

                <div className="relative z-10 flex h-full flex-col">

                    {/* Header */}

                    <div className="flex shrink-0 items-center justify-between px-6 pt-6">

                        <div>
                            <p className="text-base font-bold text-gray-900">
                                Complete Downpayment
                            </p>
                        </div>

                        <img
                            src="/CleverPeLogos/CleverPE_Black_icon.svg"
                            alt="CleverPe"
                            className="h-6 w-6 opacity-90"
                        />

                    </div>

                    {/* Content */}

                    <div className="flex flex-1 flex-col px-6 pb-4 pt-7">

                        {/* Intro */}

                        <p className="text-[13px] leading-snug text-gray-500">

                            <span className="block font-semibold text-gray-800">
                                You're almost there!
                            </span>

                            <span className="mt-0.5 block">
                                Just one step away from confirming your order.
                            </span>

                        </p>

                        {/* Amount */}

                        <div className="flex flex-1 flex-col items-center justify-center">

                            <div className="relative w-full max-w-xs overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-blue-50 px-6 py-8 shadow-[0_4px_12px_rgba(15,23,42,0.06)]">

                                <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-indigo-200/30" />

                                <div className="pointer-events-none absolute -bottom-8 -left-6 h-20 w-20 rounded-full bg-blue-200/30" />

                                <div className="relative z-10 flex flex-col items-center">

                                    <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-400">
                                        Down Payment
                                    </span>

                                    <span className="mt-2 text-4xl font-bold leading-tight text-gray-900">
                                        {formatAmount(
                                            journey.dpAmount
                                        )}
                                    </span>

                                    <div className="mt-4 h-px w-16 bg-indigo-200" />

                                    <span className="mt-3 text-center text-[11.5px] text-gray-500">
                                        Pay this amount to proceed
                                    </span>

                                </div>

                            </div>

                        </div>

                        {/* Payment Error */}

                        {paymentError && (
                            <div className="mb-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3">

                                <div className="flex items-center justify-center gap-2">

                                    <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />

                                    <p className="text-center text-[11.5px] font-medium text-red-600">
                                        {paymentError}
                                    </p>

                                </div>

                            </div>
                        )}

                        {/* Pay Now */}

                        <button
                            onClick={handlePayNow}
                            disabled={isPaying}
                            className="mb-4 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.99] disabled:opacity-60"
                        >

                            {isPaying && (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            )}

                            {isPaying
                                ? "Processing Payment..."
                                : "Pay Now"}

                        </button>

                        <PoweredByFooter />

                    </div>

                </div>

            </div>
        </MobileFrame>
    );
};

export default DownPayment;